import {
    ASSESSMENT_ASSIGNMENT_ROLES, AssessmentAssignmentRole, AssessmentAssignmentResult,
    OrganizationRole, UserAccessContext, UserAccessQuery, UserAccessCapabilities, UserManagementCapabilities, UserAssessmentSummary,
    AccessUserIdentity, AssessmentInvitationPayload, ExistingMemberOffer, PersistedInvitationResult, InvitationResendResult,
    StorageOnlyConversionOffer, StorageOnlyConversionResult
} from "./assessment_user_access.types";

type RecordValue = Record<string, unknown>;
export class UserAccessError extends Error {
    constructor(message: string, readonly code: string = "INVALID_RESPONSE",
        readonly conflictingRole: AssessmentAssignmentRole | null = null,
        readonly existingMember: ExistingMemberOffer | null = null,
        readonly storageOnlyConversion: StorageOnlyConversionOffer | null = null) { super(message); }
}
const invalid = (): never => { throw new UserAccessError("User access data is unavailable. Please refresh and try again."); };
export const accessRecord = (value: unknown): RecordValue =>
    value !== null && typeof value === "object" && !Array.isArray(value) ? value as RecordValue : invalid();
export const accessId = (value: unknown): number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0 ? value : invalid();
const nullableId = (value: unknown): number | null => value === null ? null : accessId(value);
const text = (value: unknown): string => typeof value === "string" && value.length <= 1024 ? value : invalid();
const bool = (value: unknown): boolean => typeof value === "boolean" ? value : invalid();
const array = (value: unknown): unknown[] => Array.isArray(value) && value.length <= 10000 ? value : invalid();
const role = (value: unknown): AssessmentAssignmentRole =>
    ASSESSMENT_ASSIGNMENT_ROLES.includes(value as AssessmentAssignmentRole) ? value as AssessmentAssignmentRole : invalid();
export const assignmentRoles = (value: unknown): AssessmentAssignmentRole[] => {
    const roles = array(value).map(role);
    return roles.length <= 6 && new Set(roles).size === roles.length ? roles : invalid();
};
const organizationRole = (value: unknown): OrganizationRole =>
    ["User", "Admin", "Global Admin", "Storage Only"].includes(value as string) ? value as OrganizationRole : invalid();
export const normalizeUserManagementCapabilities = (value: unknown): UserManagementCapabilities => {
    const v = accessRecord(value);
    return { canEditProfile: bool(v.canEditProfile), canManageAccount: bool(v.canManageAccount),
        canManageAssessmentAssignments: bool(v.canManageAssessmentAssignments), canResendInvitation: bool(v.canResendInvitation) };
};
export const normalizeUserAccessCapabilities = (value: unknown): UserAccessCapabilities => {
    const c = accessRecord(value);
    return { canViewOrganizationUsers: bool(c.canViewOrganizationUsers), canInviteWithoutAssessment: bool(c.canInviteWithoutAssessment),
        canInviteOnAssessment: bool(c.canInviteOnAssessment), hasEditableAssessments: bool(c.hasEditableAssessments),
        invitationOrganizationRoles: array(c.invitationOrganizationRoles).map(organizationRole) };
};
const summary = (value: unknown): UserAssessmentSummary => {
    const v = accessRecord(value);
    return { productLicenseId: accessId(v.productLicenseId), merchantId: nullableId(v.merchantId),
        merchantName: text(v.merchantName), formType: text(v.formType), displayName: text(v.displayName),
        assignmentRoles: assignmentRoles(v.assignmentRoles), readOnly: bool(v.readOnly) };
};
export const accessData = (value: unknown): RecordValue => {
    const envelope = accessRecord(value);
    if (envelope.success !== true) throw normalizeUserAccessError({ error: envelope });
    return accessRecord(envelope.data);
};
export function normalizeUserAccessError(error: unknown): UserAccessError {
    if (error instanceof UserAccessError) return error;
    const outer = error && typeof error === "object" ? error as RecordValue : {};
    const inner = outer.error && typeof outer.error === "object" && !Array.isArray(outer.error)
        ? outer.error as RecordValue : {};
    const message = typeof inner.message === "string" && inner.message.length <= 1024
        ? inner.message : "Unable to update user access. Please refresh and try again.";
    const code = typeof inner.errorCode === "string" && /^[A-Z_]{1,80}$/.test(inner.errorCode)
        ? inner.errorCode : "USER_ACCESS_UNAVAILABLE";
    const conflict = ASSESSMENT_ASSIGNMENT_ROLES.includes(inner.conflictingRole as AssessmentAssignmentRole)
        ? inner.conflictingRole as AssessmentAssignmentRole : null;
    return new UserAccessError(message, code, conflict);
}
export function normalizeUserAccess(value: unknown, accountId: number, actorUserId: number, query: UserAccessQuery): UserAccessContext {
    const v = accessData(value);
    if (accessId(v.accountId) !== accountId || accessId(v.actorUserId) !== actorUserId) return invalid();
    const target = v.target === null ? null : (() => {
        const t = accessRecord(v.target);
        return { userId: accessId(t.userId), displayName: text(t.displayName), organizationRole: organizationRole(t.organizationRole),
            capabilities: normalizeUserManagementCapabilities(t.capabilities), assessmentAssignments: array(t.assessmentAssignments).map(summary) };
    })();
    const assessment = v.assessment === null ? null : (() => {
        const a = accessRecord(v.assessment);
        return { ...summary(a), allowedRoleChoices: assignmentRoles(a.allowedRoleChoices),
            primaryHolders: array(a.primaryHolders).map((entry) => {
                const h = accessRecord(entry);
                return { role: role(h.role), userId: nullableId(h.userId), displayName: h.displayName === null ? null : text(h.displayName) };
            }), roleConflicts: array(a.roleConflicts).map((entry) => {
                const r = accessRecord(entry);
                return { role: role(r.role), code: text(r.code), conflictingRole: r.conflictingRole === null ? null : role(r.conflictingRole) };
            }) };
    })();
    if ((target?.userId ?? null) !== (query.targetUserId ?? null) ||
        (assessment?.productLicenseId ?? null) !== (query.productLicenseId ?? null)) return invalid();
    return { accountId, actorUserId, target, assessment, capabilities: normalizeUserAccessCapabilities(v.capabilities) };
}
export function normalizeAssignmentResult(value: unknown, accountId: number, productLicenseId: number, targetUserId: number): AssessmentAssignmentResult {
    const v = accessData(value);
    if (accessId(v.accountId) !== accountId || accessId(v.productLicenseId) !== productLicenseId ||
        accessId(v.targetUserId) !== targetUserId) return invalid();
    return { accountId, productLicenseId, targetUserId, assignmentRoles: assignmentRoles(v.assignmentRoles),
        changed: bool(v.changed), actorRetainsAssessmentAccess: bool(v.actorRetainsAssessmentAccess) };
}

const userIdentity = (value: unknown, accountId: number, targetUserId?: number): AccessUserIdentity => {
    const u = accessRecord(value);
    if (accessId(u.accountId) !== accountId || (targetUserId !== undefined && accessId(u.id) !== targetUserId)) return invalid();
    return { id: accessId(u.id), accountId, displayName: text(u.displayName), email: text(u.email), role: organizationRole(u.role) };
};

export function invitationRequest(payload: AssessmentInvitationPayload): AssessmentInvitationPayload {
    if (typeof payload.submissionKey !== "string" || !/^[a-zA-Z0-9_-]{16,128}$/.test(payload.submissionKey) ||
        payload.role === ("Storage Only" as OrganizationRole)) return invalid();
    const body: AssessmentInvitationPayload = { displayName: text(payload.displayName), email: text(payload.email),
        role: organizationRole(payload.role) as AssessmentInvitationPayload["role"], submissionKey: payload.submissionKey };
    if (payload.phoneNumber !== undefined) body.phoneNumber = payload.phoneNumber === null ? null : text(payload.phoneNumber);
    if (payload.certificateNumber !== undefined) body.certificateNumber = payload.certificateNumber === null ? null : text(payload.certificateNumber);
    if (payload.productLicenseId !== undefined) {
        body.productLicenseId = accessId(payload.productLicenseId);
        body.desiredRoles = assignmentRoles(payload.desiredRoles);
        if (!body.desiredRoles.length) return invalid();
    } else if (payload.desiredRoles !== undefined || payload.assignExistingUserId !== undefined) return invalid();
    if (payload.assignExistingUserId !== undefined) body.assignExistingUserId = accessId(payload.assignExistingUserId);
    return body;
}

export function normalizeInvitationResult(value: unknown, accountId: number,
    request: AssessmentInvitationPayload): PersistedInvitationResult {
    const envelope = accessRecord(value), v = accessRecord(envelope.data);
    const outcome = v.outcome;
    if (outcome !== "invited" && outcome !== "existing_user_assigned" && outcome !== "delivery_failed" && outcome !== "delivery_unknown") return invalid();
    const expectedDelivery = outcome === "invited" ? "sent" : outcome === "existing_user_assigned" ? "not_requested"
        : outcome === "delivery_failed" ? "failed" : outcome === "delivery_unknown" ? "pending" : invalid();
    const deliveryFailure = outcome === "delivery_failed" || outcome === "delivery_unknown";
    if (envelope.success !== !deliveryFailure || v.deliveryState !== expectedDelivery ||
        (deliveryFailure && envelope.errorCode !== (outcome === "delivery_failed" ? "INVITATION_DELIVERY_FAILED" : "INVITATION_DELIVERY_UNKNOWN")) ||
        accessId(v.accountId) !== accountId || nullableId(v.productLicenseId) !== (request.productLicenseId ?? null) ||
        v.recovery !== (deliveryFailure ? "resend_invitation" : null) ||
        (outcome === "existing_user_assigned") !== (request.assignExistingUserId !== undefined)) return invalid();
    return { outcome, user: userIdentity(v.user, accountId, request.assignExistingUserId), accountId,
        productLicenseId: nullableId(v.productLicenseId), assignmentRoles: assignmentRoles(v.assignmentRoles),
        duplicate: bool(v.duplicate), assignmentChanged: outcome === "existing_user_assigned" ? bool(v.assignmentChanged)
            : request.productLicenseId !== undefined && !bool(v.duplicate),
        deliveryState: expectedDelivery, recovery: deliveryFailure ? "resend_invitation" : null };
}

export function normalizeInvitationError(error: unknown, accountId: number,
    productLicenseId: number | null): UserAccessError {
    const normalized = normalizeUserAccessError(error);
    if (normalized.code === "INVITE_EXISTING_STORAGE_ONLY_CONFLICT" && productLicenseId === null) {
        const d = accessRecord(accessRecord(accessRecord(error).error).data), u = accessRecord(d.existingUser);
        if (d.conflictType !== "existing_same_account_storage_only_user" || accessId(u.accountId) !== accountId ||
            u.role !== "Storage Only") return invalid();
        return new UserAccessError(normalized.message, normalized.code, null, null, {
            userId: accessId(u.id), accountId, assignedMerchantNames: array(u.assignedMerchantNames).map(text)
        });
    }
    if (normalized.code !== "INVITATION_EXISTING_MEMBER") return normalized;
    const envelope = accessRecord(accessRecord(error).error), d = accessRecord(envelope.data), u = accessRecord(d.existingUser);
    if (d.conflictType !== "existing_same_account_user" || nullableId(d.productLicenseId) !== productLicenseId ||
        accessId(u.accountId) !== accountId) return invalid();
    const existingMember: ExistingMemberOffer = { conflictType: "existing_same_account_user",
        canAssignToAssessment: bool(d.canAssignToAssessment), productLicenseId,
        existingUser: { id: accessId(u.id), accountId, displayName: text(u.displayName), role: organizationRole(u.role) } };
    return new UserAccessError(normalized.message, normalized.code, normalized.conflictingRole, existingMember);
}

export function normalizeStorageOnlyConversion(value: unknown, accountId: number, userId: number): StorageOnlyConversionResult {
    const v = accessData(value), user = userIdentity(v.user, accountId, userId);
    if (user.role === "Storage Only") return invalid();
    return { outcome: "storage_user_converted", user };
}

export function normalizeResendResult(value: unknown, accountId: number, targetUserId: number): InvitationResendResult {
    const envelope = accessRecord(value), v = accessRecord(envelope.data);
    const sent = v.deliveryState === "sent";
    if ((!sent && v.deliveryState !== "failed") || envelope.success !== sent || v.resent !== sent || v.tokenReplaced !== true ||
        (!sent && envelope.errorCode !== "RESEND_DELIVERY_FAILED")) return invalid();
    return { user: userIdentity(v.user, accountId, targetUserId), resent: sent, tokenReplaced: true, deliveryState: sent ? "sent" : "failed" };
}

// Only these fixed delivery errors contain a committed result. All other errors stay errors.
export function deliveryErrorEnvelope(error: unknown, codes: readonly string[]): unknown | null {
    if (!error || typeof error !== "object") return null;
    const e = (error as RecordValue).error;
    if (!e || typeof e !== "object" || Array.isArray(e)) return null;
    const v = e as RecordValue;
    return v.success === false && codes.includes(v.errorCode as string) ? v : null;
}
